import {
    Paragraph,
    TextRun,
    TableRow,
    TableCell,
    AlignmentType,
    ShadingType,
} from "docx";
import tcLib from '@cclib/lib/timecode.js';
import { calculateDuration } from '@cclib/lib/styleGuideTests.js';

/**
 * Shading preset for light blue header cells (condensed, starter templates).
 */
export const SHADING_LIGHT = {
    fill: "d1e0f0",
    type: ShadingType.CLEAR,
    color: "auto",
};

/**
 * Shading preset for blue-tinted data/header cells (default, condensed templates).
 */
export const SHADING_DARK = {
    fill: "dce9f7",
    type: ShadingType.CLEAR,
    color: "auto",
};

/**
 * Creates a table header cell with bold text.
 * @param {string} text - Header label text
 * @param {string} [alignment=AlignmentType.LEFT] - Paragraph alignment
 * @param {object} [shading] - Optional shading config object
 * @param {number} [columnSpan] - Optional column span
 * @returns {TableCell}
 */
export function createHeaderCell(text, alignment = AlignmentType.LEFT, shading, columnSpan) {
    const cellOptions = {
        children: [new Paragraph({
            children: [new TextRun({ text, bold: true })],
            alignment,
        })]
    };
    if (shading) cellOptions.shading = shading;
    if (columnSpan) cellOptions.columnSpan = columnSpan;
    return new TableCell(cellOptions);
}

/**
 * Creates a simple table header cell using paragraph-level text (no bold TextRun, just Paragraph text prop).
 * Used by the default template's second header row.
 * @param {string} text - Header label text
 * @param {string} [alignment=AlignmentType.CENTER] - Paragraph alignment
 * @param {object} [shading] - Optional shading config
 * @param {number} [columnSpan] - Optional column span
 * @returns {TableCell}
 */
export function createSimpleHeaderCell(text, alignment = AlignmentType.CENTER, shading, columnSpan) {
    const cellOptions = {
        children: [new Paragraph({ text, alignment })]
    };
    if (shading) cellOptions.shading = shading;
    if (columnSpan) cellOptions.columnSpan = columnSpan;
    return new TableCell(cellOptions);
}

/**
 * Creates a data cell containing one or more Paragraphs.
 * @param {string|Paragraph|Array<Paragraph>} content - Cell content
 * @param {object} [options] - Additional TableCell options (rowSpan, shading, columnSpan)
 * @returns {TableCell}
 */
export function createDataCell(content, options = {}) {
    let children;
    if (typeof content === "string") {
        children = [new Paragraph(content)];
    } else if (Array.isArray(content)) {
        children = content;
    } else {
        children = [content];
    }
    return new TableCell({ children, ...options });
}

/**
 * Creates an empty cell with optional shading.
 * @param {object} [shading] - Optional shading config
 * @returns {TableCell}
 */
export function createEmptyCell(shading) {
    const cellOptions = { children: [new Paragraph("")] };
    if (shading) cellOptions.shading = shading;
    return new TableCell(cellOptions);
}

/**
 * Formats an absolute time (in seconds) as timecode using the project's incode offset.
 * @param {number} seconds - Time in seconds (event.start or event.end)
 * @param {object} project - Object with { incode, frameRate, dropFrame }
 * @returns {string} Formatted timecode string
 */
export function formatTimecode(seconds, project) {
    return tcLib.secToTc(
        parseFloat(seconds) + parseFloat(project.incode),
        project.frameRate,
        project.dropFrame
    );
}

/**
 * Formats the duration between two time points as timecode.
 * @param {number} start - Start time in seconds
 * @param {number} end - End time in seconds
 * @param {object} project - Object with { frameRate, dropFrame }
 * @returns {string} Formatted duration timecode string
 */
export function formatDuration(start, end, project) {
    return tcLib.secToTc(
        calculateDuration(start, end),
        project.frameRate,
        project.dropFrame
    );
}

/**
 * Extracts speaker and recipient name strings from an event.
 * @param {object} event - Event object with speakers and recipients arrays
 * @returns {{ speakerNames: string, recipientNames: string }}
 */
export function getEventNames(event) {
    const speakerNames = (event.speakers || []).map(s => s.name).join(", ");
    const recipientNames = (event.recipients || []).map(r => r.name).join(", ");
    return { speakerNames, recipientNames };
}

/**
 * Creates a TableRow from an array of TableCells.
 * @param {Array<TableCell>} cells - Array of TableCell objects
 * @param {object} [options] - Additional TableRow options (e.g. tableHeader)
 * @returns {TableRow}
 */
export function createRow(cells, options = {}) {
    return new TableRow({ children: cells, ...options });
}

/**
 * Formats an event's tags as an array of TextRun objects.
 * Each tag is rendered as [*term*] with italic term text, separated by spaces.
 * Returns an empty array if no tags are present.
 * @param {Array<{term: string}>} [tags] - Array of Tag objects
 * @returns {TextRun[]}
 */
export function formatTagRuns(tags) {
    if (!tags || tags.length === 0) return [];
    const runs = [];
    tags.forEach((tag, index) => {
        runs.push(new TextRun({ text: "[" }));
        runs.push(new TextRun({ text: tag.term, italics: true }));
        runs.push(new TextRun({ text: "]" + (index < tags.length - 1 ? " " : "") }));
    });
    return runs;
}

/**
 * Builds an array of Paragraphs with an optional tag line prepended.
 * If the event has tags, a tag paragraph is inserted before the content
 * with a line break separating them. Otherwise returns just the content.
 * @param {Array<{term: string}>} [tags] - Array of Tag objects from the event
 * @param {string|Paragraph|Array<Paragraph>} content - Cell content (string, Paragraph, or array of Paragraphs)
 * @returns {Array<Paragraph>}
 */
export function buildContentWithTags(tags, content) {
    const tagRuns = formatTagRuns(tags);
    const contentParagraphs = typeof content === "string"
        ? [new Paragraph(content)]
        : Array.isArray(content)
            ? content
            : [content];

    if (tagRuns.length === 0) return contentParagraphs;

    return [
        new Paragraph({ children: tagRuns }),
        ...contentParagraphs,
    ];
}
